import { useState, type FormEvent, type ReactNode } from "react";

import { Button } from "@/components/ui/Button";

function QuickAddSpinner() {
  return (
    <span
      aria-hidden
      className="inline-block size-3.5 animate-spin rounded-full border-2 border-zinc-900 border-r-transparent"
    />
  );
}

const inputClassName =
  "w-full min-w-0 rounded-xl border border-black/10 bg-white px-3 py-1.5 text-sm text-whart-text outline-none placeholder:text-neutral-400 focus:border-zinc-900 disabled:opacity-50";

interface LibraryQuickAddRowProps {
  inputId: string;
  label: string;
  placeholder: string;
  submitLabel: string;
  creatingLabel: string;
  isPending: boolean;
  error?: string | null;
  onSubmit: (value: string) => Promise<void>;
  trailingCells?: ReactNode;
}

export function LibraryQuickAddPlaceholderCell({
  children = "—",
  isLast = false,
}: {
  children?: ReactNode;
  isLast?: boolean;
}) {
  return (
    <td
      className={
        isLast
          ? "px-4 py-3 text-neutral-400"
          : "border-r border-black/5 px-4 py-3 text-neutral-400"
      }
    >
      {children}
    </td>
  );
}

export function LibraryQuickAddRow({
  inputId,
  label,
  placeholder,
  submitLabel,
  creatingLabel,
  isPending,
  error = null,
  onSubmit,
  trailingCells,
}: LibraryQuickAddRowProps) {
  const [value, setValue] = useState("");

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    event.stopPropagation();

    const trimmed = value.trim();
    if (!trimmed || isPending) {
      return;
    }

    void onSubmit(trimmed)
      .then(() => {
        setValue("");
      })
      .catch(() => {
        // Errors are surfaced by the parent via the error prop.
      });
  }

  return (
    <tr
      className="border-b border-black/10 bg-amber-50/30"
      onClick={(event) => event.stopPropagation()}
      onKeyDown={(event) => event.stopPropagation()}
    >
      <td className="border-r border-black/5 px-4 py-2">
        <form onSubmit={handleSubmit} className="flex min-w-0 items-center gap-2">
          <label htmlFor={inputId} className="sr-only">
            {label}
          </label>
          <input
            id={inputId}
            type="text"
            value={value}
            disabled={isPending}
            placeholder={placeholder}
            onChange={(event) => setValue(event.target.value)}
            className={inputClassName}
          />
          <Button
            type="submit"
            disabled={isPending || value.trim().length === 0}
            aria-busy={isPending}
            className="shrink-0 px-3 py-1.5 text-xs"
          >
            {isPending ? <QuickAddSpinner /> : null}
            {isPending ? creatingLabel : submitLabel}
          </Button>
        </form>
        {error ? (
          <p role="alert" className="mt-1 text-xs text-whart-urgent">
            {error}
          </p>
        ) : null}
      </td>
      {trailingCells}
    </tr>
  );
}
